import React, { ChangeEvent } from 'react';
import { Box, Checkbox, FormControlLabel, Typography, Theme } from '@material-ui/core';
import { AccessTime, Person } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { AvailabilityCenterSession_currentFlexibleInstances as FlexibleInstance } from './__generated__/AvailabilityCenterSession';
import { getDisplayTimeSchedule } from '../../functions/activity-display-helpers';
import { CapacityStatusChip } from '../../chips/CapacityStatusChip';
import { SessionCapacityStatus } from '../../hooks/useActivityCapacityStatus';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      color: theme.color.grey.neutral400,
      fontWeight: 800,
      width: '100%',
      letterSpacing: '-0.02em',
    },
    subTitle: {
      fontSize: 12,
      color: theme.color.grey.neutral200,
    },
    subTitlesContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    subTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '22px',
    },
    captionText: {
      fontSize: 7,
      color: theme.color.grey.neutral200,
    },
    captionContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    container: {
      width: '100%',
      paddingTop: '20px',
      paddingBottom: '20px',
      border: '2px solid',
      borderColor: theme.color.grey.border,
      backgroundColor: theme.color.grey.background,
      borderRadius: '8px',
      marginBottom: '25px',
    },
    checkBox: {
      marginLeft: '9px',
      marginRight: '15px',
    },
    icon: {
      marginRight: '7px',
      color: theme.color.grey.neutral200,
    },
  })
);

interface Props {
  session: AvailabilityCenterSession;
  isSelected?: boolean;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  capacityStatus?: SessionCapacityStatus;
}

interface AvailabilityCenterSession {
  __typename: 'VOLUNTEER_SessionType';
  sessionId: string;
  name: string;
  minVolunteers: number | null;
  maxVolunteers: number | null;
  startTime: any | null;
  endTime: any | null;
  dateRemoved: any | null;
  isFlexible?: boolean;
  currentFlexibleInstances?: FlexibleInstance[];
}

interface SessionProps {
  session: AvailabilityCenterSession;
  currentDate?: string;
  capacityStatus?: SessionCapacityStatus;
}

export function Session({ session, currentDate, capacityStatus }: SessionProps) {
  const classes = useStyles();

  // Find a flexible instance that matches the current date, if provided
  const matchingInstance = React.useMemo(() => {
    if (!session.isFlexible || !session.currentFlexibleInstances || !session.currentFlexibleInstances.length) {
      return null;
    }

    // If no date is provided, don't display any specific instance
    if (!currentDate) {
      return null;
    }

    // Find an instance matching the current date
    return session.currentFlexibleInstances.find(
      (instance) => instance && (!instance.sessionDate || instance.sessionDate === currentDate)
    );
  }, [session, currentDate]);

  // Check if this is a flexible session with matching instance
  const hasMatchingInstance = Boolean(matchingInstance);

  // Get time display based on whether it's a flexible instance or regular session
  let sessionTime;
  let displayName = session.name;

  if (hasMatchingInstance && matchingInstance) {
    // Use the matching instance's time if available
    if (matchingInstance.startTime && matchingInstance.endTime) {
      sessionTime = getDisplayTimeSchedule(
        matchingInstance.startTime,
        matchingInstance.endTime,
        true,
        matchingInstance
      );
    } else {
      sessionTime = getDisplayTimeSchedule(session.startTime, session.endTime);
    }

    // Use the instance's name if available
    if (matchingInstance.name) {
      displayName = matchingInstance.name;
    }
  } else {
    sessionTime = getDisplayTimeSchedule(session.startTime, session.endTime);
  }

  return (
    <Box>
      <Typography variant='h6' className={classes.title}>
        {displayName}
        {hasMatchingInstance && displayName !== session.name && (
          <Typography variant='caption' component='span' style={{ marginLeft: '8px', color: '#666' }}>
            ({session.name})
          </Typography>
        )}
      </Typography>
      <Box>
        <Box className={classes.subTitlesContainer}>
          <Box className={classes.subTitleContainer}>
            <AccessTime style={{ fontSize: 17 }} className={classes.icon} />
            <Typography className={classes.subTitle}>{sessionTime}</Typography>
          </Box>
          <Box className={classes.subTitleContainer}>
            <Box className={`${classes.captionContainer} ${classes.icon}`}>
              <Person style={{ fontSize: 17 }} />
              <Typography className={classes.captionText}>MIN</Typography>
            </Box>
            <Typography className={classes.subTitle}>{session.minVolunteers || 1}</Typography>
          </Box>
          {session.maxVolunteers && (
            <Box className={classes.subTitleContainer}>
              <Box className={`${classes.captionContainer} ${classes.icon}`}>
                <Person style={{ fontSize: 17 }} />
                <Typography className={classes.captionText}>MAX</Typography>
              </Box>
              <Typography className={classes.subTitle}>{session.maxVolunteers}</Typography>
            </Box>
          )}
          {capacityStatus?.status && (
            <Box className={classes.subTitleContainer}>
              <CapacityStatusChip
                status={capacityStatus.status}
                rosteredVolunteers={capacityStatus.rosteredVolunteers}
                maxVolunteers={capacityStatus.maxVolunteers}
                minVolunteers={capacityStatus.minVolunteers}
                size='tiny'
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function SessionItem({
  session,
  isSelected,
  onSelect,
  currentDate,
  capacityStatus,
}: Props & { currentDate?: string }) {
  const classes = useStyles();

  return (
    <FormControlLabel
      className={classes.container}
      control={
        <Checkbox
          className={classes.checkBox}
          checked={isSelected}
          name={session.sessionId}
          onChange={onSelect}
          color='primary'
        />
      }
      label={<Session session={session} currentDate={currentDate} capacityStatus={capacityStatus} />}
    />
  );
}
